import { useModal } from 'modals';

import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import DeferredResearchGraph from '../HomePage/DeferredResearchGraph';
import { firstContentSpacing } from '../first-content-spacing';
import { pageFrame } from '../page-frame';
import {
  type ResearchMedia,
  type ResearchPaper,
  researchPageContent,
} from './content';

interface PaperMediaProps {
  media: ResearchMedia;
}

function PaperMedia({ media }: PaperMediaProps) {
  return (
    <div className="bg-primary aspect-video w-full min-w-0 overflow-hidden rounded-lg">
      <img
        src={media.src}
        alt={media.description}
        loading="lazy"
        className="h-full w-full object-cover"
      />
    </div>
  );
}

interface PaperCardProps {
  paper: ResearchPaper;
}

function PaperCard({ paper }: PaperCardProps) {
  const className =
    'bg-secondary flex min-w-0 flex-col gap-10 rounded-2xl p-8 2xl:grid 2xl:grid-cols-2 2xl:items-start';
  return (
    <a
      className={`${className} no-underline hover:no-underline`}
      href={paper.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <PaperMedia media={paper.media} />
      <div className="flex min-w-0 flex-col gap-4">
        <Text as="h3" token="24" leading="editorial" className="text-pretty">
          {paper.title}
        </Text>
        <Text
          as="p"
          token="24"
          leading="editorial"
          className="text-secondary text-pretty"
        >
          {paper.description}
        </Text>
        <Button as="span" variant="cta" className="w-fit">
          Read the paper
        </Button>
      </div>
    </a>
  );
}

function Intro() {
  const { intro } = researchPageContent;
  const [firstParagraph, secondParagraph] = intro.paragraphs;

  return (
    <section
      className={`border-primary border-b px-4 pb-10 ${firstContentSpacing} sm:px-12 sm:pb-20`}
    >
      <div className="flex flex-col gap-10">
        <Text
          as="p"
          token={{ base: '24/medium', sm: '32/medium' }}
          leading="editorial"
        >
          {intro.label}
        </Text>
        <Text
          as="h1"
          token={{
            base: 'display/32/bold',
            sm: 'display/40/bold',
            lg: 'display/56/bold',
            xl: 'display/72/bold',
            '2xl': 'display/88/bold',
          }}
          leading="editorial"
          className="max-w-[1749px] py-10 !tracking-normal"
        >
          <span>{intro.title.prefix}</span>
          <br />
          <span className="text-green">{intro.title.emphasized}</span>
          <span className="2xl:hidden"> {intro.title.suffix}</span>
          <span className="hidden 2xl:inline">
            <br />
            {intro.title.suffix}
          </span>
        </Text>
        <div className="grid gap-6 md:grid-cols-2 2xl:grid-cols-[813px_813px]">
          <Text
            as="p"
            token="24"
            leading="editorial"
            className="text-secondary"
          >
            {firstParagraph}
          </Text>
          <Text
            as="p"
            token="24"
            leading="editorial"
            className="text-secondary"
          >
            {secondParagraph}
          </Text>
        </div>
      </div>
    </section>
  );
}

function ScalingAndMeasurement() {
  const { measurement, scaling } = researchPageContent;
  const [firstParagraph, secondParagraph] = scaling.paragraphs;

  return (
    <section className="border-primary flex flex-col gap-10 border-b px-4 py-10 sm:px-12 sm:py-20">
      <div className="flex flex-col gap-10">
        <Text
          as="h2"
          token={{ base: '24/medium', sm: '32/medium' }}
          leading="editorial"
        >
          {scaling.title}
        </Text>
        <Text
          as="p"
          token={{ base: '24', sm: '32' }}
          leading="editorial"
          className="text-secondary max-w-[813px] text-pretty"
        >
          {scaling.description}
        </Text>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_648px] 2xl:grid-cols-[816px_648px]">
          <Text
            as="p"
            token="24"
            leading="editorial"
            className="text-secondary text-pretty"
          >
            {firstParagraph}
          </Text>
          <Text
            as="p"
            token="24"
            leading="editorial"
            className="text-secondary text-pretty"
          >
            {secondParagraph}
          </Text>
        </div>
      </div>
      <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_312px] xl:items-start 2xl:grid-cols-[minmax(0,1488px)_312px]">
        <DeferredResearchGraph featured />
        <div
          aria-hidden="true"
          className="bg-primary hidden h-32 rounded-lg xl:block"
        />
      </div>
      <div className="flex max-w-[816px] flex-col gap-10">
        <Text
          as="h2"
          token={{ base: '24/medium', sm: '32/medium' }}
          leading="editorial"
        >
          {measurement.title}
        </Text>
        <Text
          as="p"
          token="24"
          leading="editorial"
          className="text-secondary text-pretty"
        >
          {measurement.description}
        </Text>
      </div>
    </section>
  );
}

function Papers() {
  const { papers } = researchPageContent;

  return (
    <section className="border-primary flex flex-col gap-10 border-b px-4 py-10 sm:px-12 sm:py-20">
      <div className="flex max-w-[813px] flex-col gap-10">
        <Text
          as="h2"
          token={{ base: '24/medium', sm: '32/medium' }}
          leading="editorial"
        >
          {papers.title}
        </Text>
        <Text
          as="p"
          token={{ base: '24', sm: '32' }}
          leading="editorial"
          className="text-secondary text-pretty"
        >
          {papers.description}
        </Text>
      </div>
      <div className="grid gap-10 lg:grid-cols-2">
        {papers.items.map((paper) => (
          <PaperCard key={paper.id} paper={paper} />
        ))}
      </div>
    </section>
  );
}

function Contact() {
  const { open } = useModal();
  const { contact } = researchPageContent;

  return (
    <section className="border-primary flex flex-col gap-10 border-b px-4 py-10 sm:px-12 sm:py-20">
      <Text
        as="p"
        token={{ base: '24/medium', sm: '32/medium' }}
        leading="editorial"
      >
        {contact.label}
      </Text>
      <Text
        as="h2"
        token={{
          base: 'display/32/bold',
          sm: 'display/40/bold',
          lg: 'display/56/bold',
          xl: 'display/72/bold',
          '2xl': 'display/88/bold',
        }}
        leading="editorial"
        className="max-w-[1749px] py-10 !tracking-normal"
      >
        <span>{contact.title.prefix}</span>
        <span className="2xl:hidden"> </span>
        <span className="hidden 2xl:inline">
          <br />
        </span>
        <span className="text-green">{contact.title.emphasized}</span>
        {contact.title.suffix}
      </Text>
      <Button
        type="button"
        variant="cta"
        className="w-fit"
        onClick={() => open('ENTERPRISE_CONTACT', {})}
      >
        {contact.buttonLabel}
      </Button>
    </section>
  );
}

export default function ResearchPage() {
  return (
    <div className={pageFrame}>
      <Intro />
      <ScalingAndMeasurement />
      <Papers />
      <Contact />
    </div>
  );
}
